import type {
  GetQBankStructureLevelItemsByQBankStructureIdQuery,
  GetQBankStructureLevelsByQBankStructureIdQuery,
} from '@exam-management/graphql';
import { LocationFormFields } from '@exam-management/qcontent-management';
import { Box, Button, Divider, Flex, Radio, Text } from '@shared/components';
import { useMemo } from 'react';

import { EMPTY_LOCATION } from '../../constants/question-selection';
import { useQuestionReviewTaskCreationFormContext } from '../../providers';

interface SelectLocationFilterProps {
  qbsLevelItems: GetQBankStructureLevelItemsByQBankStructureIdQuery;
  qbsLevel: GetQBankStructureLevelsByQBankStructureIdQuery;
  questionType: 'single' | 'item-set';
  setQuestionType: (questionType: 'single' | 'item-set') => void;
}

export default function SelectLocationFilter(props: Readonly<SelectLocationFilterProps>) {
  const { qbsLevelItems, qbsLevel, questionType, setQuestionType } = props;
  const formContext = useQuestionReviewTaskCreationFormContext();

  const resetLocation = () => {
    formContext.setValue('location', EMPTY_LOCATION);
  };

  const { qBankStructureLevels, qBankStructureLevelItemsData } = useMemo(() => {
    const levels = [...(qbsLevel?.qbsGetQBankStructureLevelsByQBankStructureId ?? [])]
      .sort((a, b) => a.level - b.level)
      .filter((level) => level.level !== 1);

    const items = (qbsLevelItems?.qbsGetQBankStructureLevelItemsByQBankStructureId ?? [])
      .map((item) => {
        const qBankStructureLevel = levels.find((level) => level.id === item.qBankStructureLevelId);
        if (!qBankStructureLevel) return null;
        return {
          ...item,
          qBankStructureLevel,
        };
      })
      .filter((item) => item !== null);

    return { qBankStructureLevels: levels, qBankStructureLevelItemsData: items };
  }, [qbsLevel, qbsLevelItems]);

  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(4),
        p: theme.spacing(4),
        border: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: theme.shape.rounded(2),
        width: '360px',
        flexShrink: 0,
      })}
    >
      <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(4) }}>
        <Text variant='h4'>Question Type</Text>
        <Flex sx={{ gap: (theme) => theme.spacing(4) }}>
          <Flex
            sx={{
              alignItems: 'center',
              gap: (theme) => theme.spacing(2),
              justifyContent: 'start',
            }}
          >
            <Radio
              label='Single'
              checked={questionType === 'single'}
              onChange={() => setQuestionType('single')}
            />
          </Flex>
          <Flex sx={{ alignItems: 'center', gap: (theme) => theme.spacing(2) }}>
            <Radio
              label='Item Set'
              checked={questionType === 'item-set'}
              onChange={() => setQuestionType('item-set')}
            />
          </Flex>
        </Flex>
      </Flex>
      <Divider />
      <Flex sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <Text variant='h4'>Location</Text>
        <Button
          variant='outlined'
          size='small'
          color='secondary'
          sx={{ minWidth: 'unset' }}
          onClick={resetLocation}
        >
          Reset
        </Button>
      </Flex>
      <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(4) }}>
        <LocationFormFields
          prefix='location'
          qbsLevels={qBankStructureLevels}
          qbsLevelItems={qBankStructureLevelItemsData}
          allowSkipLevel
        />
      </Flex>
    </Box>
  );
}
